import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';

import { mapStringToCurrencySymbol } from '../../mappers/common/common';
import { Bem } from '../../service/Bem/Bem';
import { CoverageByPriceData } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import { DirectFormInput } from '../DirectFormInput/DirectFormInput';
import { ErrorItemText } from '../ErrorsPresenter/ErrorsPresenter';
import { ErrorText, ErrorTextTheme } from '../ErrorText/ErrorText';
import { InputFieldType } from '../InputField/InputField';
import { MoneyCell } from '../MoneyCell/MoneyCell';
import { PercentCell } from '../PercentCell/PercentCell';
import {
    TableColumnAlignType,
    TableColumnDataType,
    TableData,
    TableDataTheme,
    TableSizeType
} from '../TableData/TableData';

import './ContextPhrasePriceEditor.css';
import * as keyset from './ContextPhrasePriceEditor.i18n';

const i18n = i18nFactory(keyset);

export interface ContextPriceEditorProps {
    priceContext: string;
    priceForCoverage: Array<CoverageByPriceData>;
    currency: CurrencyCode;
    error?: ErrorItemText;
    priceLimitWarning?: string;
    onChange: (val: string) => void;
    onBlur: () => void;
    isCpmBanner?: boolean;
}

export class ContextPhrasePriceEditor extends React.Component<ContextPriceEditorProps> {
    render() {
        const { isCpmBanner } = this.props;

        return (
            <div className={Bem.cls('context-phrase-price-editor')}>
                {isCpmBanner ? this.getCpmBannerBody() : this.getBody()}
            </div>
        );
    }

    private getAdjustmentWarning() {
        return <div className={Bem.cls('context-phrase-price-editor', 'hint')}>{i18n('adjustments-rate')}</div>;
    }

    private getContextPriceInput = () => {
        const { priceContext, error } = this.props;

        return (
            <DirectFormInput
                key={1}
                text={priceContext}
                onChange={this.onContextPriceChange}
                onBlur={this.onContextPriceBlur}
                size={'m'}
                valid={!error}
                theme={'normal'}
                view={'classic'}
                inputType={InputFieldType.TEXT}
                errorText={
                    <ErrorText
                        theme={ErrorTextTheme.tooltip}
                        errors={error ? [{ path: '', text: error }] : undefined}
                    />
                }
            />
        );
    };

    private getContextColumns = () => {
        const { priceForCoverage, currency } = this.props;

        let priceColumnCells: Array<TableColumnDataType> = [this.getContextPriceInput()];

        if (priceForCoverage.length !== 0) {
            priceColumnCells = priceColumnCells.concat(['', '']);
        }

        return [
            {
                width: 100,
                header: i18n('context-price'),
                cells: priceColumnCells
            },
            {
                width: 105,
                header: i18n('audience_coverage'),
                align: TableColumnAlignType.right,
                cells:
                    priceForCoverage.length !== 0
                        ? priceForCoverage.map((val, key) => (
                              <PercentCell num={val.coverage} fractionalPartLength={0} key={key} />
                          ))
                        : ['-']
            },
            {
                width: 120,
                header: i18n('context-click-price'),
                align: TableColumnAlignType.right,
                cells:
                    priceForCoverage.length !== 0
                        ? priceForCoverage.map((val, key) => (
                              <MoneyCell num={val.price} currency={currency} key={key} />
                          ))
                        : ['-']
            }
        ];
    };

    private getPriceLimitWarning = () => {
        const { priceLimitWarning } = this.props;

        if (!priceLimitWarning) {
            return null;
        }

        return <div className={Bem.cls('context-phrase-price-editor', 'warning')}>{priceLimitWarning}</div>;
    };

    private getNoCoverageInfo = () => {
        const { priceForCoverage } = this.props;

        if (priceForCoverage.length !== 0) {
            return null;
        }

        return <div className={Bem.cls('context-phrase-price-editor', 'warning')}>{i18n('no-display-info')}</div>;
    };

    private getCpmBannerBody = () => {
        const { currency } = this.props;

        return (
            <div className={Bem.cls('context-phrase-price-editor', 'cpm-banner')}>
                <div className={Bem.cls('context-phrase-price-editor', 'cpm-banner-title')}>
                    {i18n('cpm-banner-title')}
                </div>
                <div className={Bem.cls('context-phrase-price-editor', 'cpm-banner-warning')}>
                    {i18n('adjustments-rate')}
                </div>
                <div className={Bem.cls('context-phrase-price-editor', 'cpm-banner-price')}>
                    <div className={Bem.cls('context-phrase-price-editor', 'cpm-banner-price-text')}>
                        {i18n('cpm-banner-set-context-price')}
                    </div>
                    <div className={Bem.cls('context-phrase-price-editor', 'cpm-banner-price-input')}>
                        {this.getContextPriceInput()}
                    </div>
                    <span className={Bem.cls('context-phrase-price-editor', 'cpm-banner-currency')}>
                        {mapStringToCurrencySymbol(currency)}
                    </span>
                </div>
            </div>
        );
    };

    private getBody = () => {
        return (
            <>
                <TableData
                    theme={TableDataTheme.dark}
                    columns={this.getContextColumns()}
                    size={TableSizeType.small}
                    rowHeight={30}
                />
                {this.getAdjustmentWarning()}
                {this.getPriceLimitWarning()}
                {this.getNoCoverageInfo()}
            </>
        );
    };

    private onContextPriceChange = (value: string) => {
        this.props.onChange(value);
    };

    private onContextPriceBlur = () => {
        this.props.onBlur();
    };
}
